/**
 * Client-side latency metrics (BLA-348).
 *
 * The backend's OpenTelemetry spans (ADR-019, `app/observability.py`) measure
 * server processing time only. Several operations have a real client-side
 * component on top of that — most notably the Sources tab's grouping/folding
 * derivation (`sourceGrouping.ts`), previously proven to be the actual
 * bottleneck while the backend was fast — so a single end-to-end number would
 * hide which side of the wire a regression (or a fix, e.g. the planned HTML
 * parser) lands on.
 *
 * This module ships a durable, machine-parseable timing line through the
 * existing `POST /api/client-logs` transport (ADR-009) so client latency joins
 * the same Cloud Logging stream the backend's `duration_ms` lines already use.
 * Entries are marked `alwaysKeep` — unlike an ordinary INFO log, a latency
 * metric that gets randomly sampled away would bias the very percentiles this
 * ticket asks for.
 *
 * **Message format** (grep/extract-friendly, no PHI — operation names and
 * counts only, never patient content):
 *
 *   `client_timing op=<operation> duration_ms=<n> <key>=<value> ...`
 *
 * See `docs/perf/latency-baseline.md` for the extraction query.
 */
import { enqueue } from "./remoteLog";

/** Extra numeric/string tags attached to a timing entry (no PHI). */
export type PerfTags = Record<string, string | number>;

/**
 * Format a client-timing message line.
 *
 * @param operation - The operation name (e.g. `"sources.client_derivation"`).
 * @param durationMs - The measured duration in milliseconds.
 * @param tags - Extra PHI-safe tags (counts, booleans-as-strings).
 * @returns The formatted, grep-friendly message string.
 */
function formatMessage(
  operation: string,
  durationMs: number,
  tags?: PerfTags,
): string {
  const parts = [
    `client_timing op=${operation}`,
    `duration_ms=${durationMs.toFixed(1)}`,
  ];
  if (tags) {
    for (const [key, value] of Object.entries(tags)) {
      parts.push(`${key}=${value}`);
    }
  }
  return parts.join(" ");
}

/**
 * Record a client-side duration as an INFO client-log entry.
 *
 * Use for a synchronous or already-measured span (e.g. a `useMemo` body
 * timed with `performance.now()` deltas).
 *
 * @param operation - The operation name.
 * @param durationMs - The measured duration in milliseconds.
 * @param tags - Extra PHI-safe tags (counts, etc.).
 * @returns None.
 */
export function recordDuration(
  operation: string,
  durationMs: number,
  tags?: PerfTags,
): void {
  enqueue({
    severity: "INFO",
    message: formatMessage(operation, durationMs, tags),
    alwaysKeep: true,
  });
}

/**
 * Time an async operation end-to-end (client wall-clock: network + server +
 * client-side handling) and record it.
 *
 * Comparing this against the matching backend `http.request` span's
 * `duration_ms` (grouped by `http_route`) isolates the client-only share —
 * network transit + response parsing/rendering — from server processing time.
 *
 * @param operation - The operation name.
 * @param fn - The async operation to time.
 * @param tags - Computes the PHI-safe tags from the resolved value (e.g. a
 *   result array's length, or whether it is an error envelope). Derived from
 *   the result rather than supplied up front because `apiUtils.fetchData`
 *   *returns* its failures instead of throwing, so the outcome is only known
 *   once the promise settles.
 * @returns Whatever `fn` resolves to; a rejection is timed and rethrown.
 */
export async function timeOperation<T>(
  operation: string,
  fn: () => Promise<T>,
  tags: (result: T) => PerfTags,
): Promise<T> {
  const start = performance.now();
  try {
    const result = await fn();
    recordDuration(operation, performance.now() - start, tags(result));
    return result;
  } catch (err) {
    recordDuration(operation, performance.now() - start, { outcome: "error" });
    throw err;
  }
}
